import { useMutation } from '@apollo/client'
import { IconButton, Menu, MenuItem } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import MoreIcon from '@material-ui/icons/MoreVert'
import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import { ConfirmActionModal } from '../../components/ConfirmActionModal'
import { DELETE_PROMOTION_MUTATION, Promotion } from '../../graphql/promotions'
import { getTranslate, lang } from '../../localization'
import useStyles from './styles/PromotionsTable.styles'

/**
 * Row actions (open, three-dot menu, delete-with-typed-confirmation) shared by the desktop and
 * mobile rows.
 *
 * The portal convention is a separate component per breakpoint (ChannelRow / ChannelRowMobile),
 * which is right for LAYOUT but would mean two copies of the delete mutation and its
 * confirmation wiring. Twin branches that must stay in step are where copy-paste defects live —
 * one branch gets a fix and the other silently does not. Layout stays duplicated; behaviour does
 * not.
 */
export const usePromotionRowActions = (
  data: Promotion,
  refetch: () => void
) => {
  const classes = useStyles()
  const history = useHistory()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false)

  const [deletePromotion, { loading: deleting }] = useMutation(
    DELETE_PROMOTION_MUTATION,
    {
      onCompleted: () => {
        toast.success(getTranslate('Promotion deleted.'))
        setDeleteModalIsOpen(false)
        refetch()
      },
      onError: () => toast.error(getTranslate('Could not delete promotion.'))
    }
  )

  const openRecord = () => history.push(`/${lang}/promotions/${data.id}`)

  const actionsCell = (
    <div>
      <IconButton
        data-cy="promotionRowMenu"
        aria-label={getTranslate('Promotion actions')}
        onClick={(event) => {
          // Without this the row's own onClick also fires and navigates away underneath the
          // menu that is opening.
          event.stopPropagation()
          setAnchorEl(event.currentTarget)
        }}>
        <MoreIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={(event) => {
            event.stopPropagation()
            setAnchorEl(null)
            openRecord()
          }}>
          {getTranslate('View')}
        </MenuItem>
        {data.canWrite && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation()
              setAnchorEl(null)
              setDeleteModalIsOpen(true)
            }}>
            {getTranslate('Delete')}
          </MenuItem>
        )}
      </Menu>
    </div>
  )

  const confirmModal = deleteModalIsOpen && (
    <ConfirmActionModal
      icon={<DeleteIcon className={classes.iconSize} />}
      title={getTranslate('The promotion is about to be deleted')}
      subtitle={getTranslate(
        'This cannot be undone. Are you sure you want to delete it?'
      )}
      // The signature is the promotion's own name, which stays visible in the row behind the
      // modal — the user can read what they must type. ConfirmActionModal keeps Confirm
      // disabled until it matches (ConfirmActionModal/index.tsx:108).
      signature={data.name}
      isOpen={deleteModalIsOpen}
      onClose={() => setDeleteModalIsOpen(false)}
      loading={deleting}
      onClick={() =>
        deletePromotion({
          variables: { id: data.id, updatedAt: data.updatedAt }
        })
      }
    />
  )

  return { openRecord, actionsCell, confirmModal }
}
